import { Lightbulb, Users, Globe, Target, ShieldCheck, HeartPulse, ArrowRight, Eye, Rocket, Star } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Helmet } from "react-helmet-async"
import { Link } from "react-router-dom"

export default function MissionVision() {
    const stats = [
        { val: "2941", label: "Étudiants (25-26)" },
        { val: "151", label: "Personnel Acad." },
        { val: "34", label: "Personnel Admin." },
        { val: "17", label: "Personnel Appui" }
    ]

    const pillars = [
        { title: "Excellence Académique", icon: Lightbulb, desc: "Programmes rigoureux alignés sur les standards internationaux pour former les leaders de demain." },
        { title: "Recherche Appliquée", icon: Globe, desc: "Production de connaissances répondant aux défis concrets de la santé et de l'agriculture burundaise." },
        { title: "Service Communautaire", icon: Users, desc: "Engagement actif dans le tissu social pour promouvoir la paix et le développement économique durable." },
        { title: "Intégrité & Éthique", icon: ShieldCheck, desc: "Promotion des valeurs morales et de l'éthique professionnelle au sein de toute notre communauté." }
    ]

    const aspirations = [
        { icon: Star, title: "Leadership Régional", description: "Devenir un pôle d'attraction pour les étudiants et chercheurs de toute la région des Grands Lacs." },
        { icon: Rocket, title: "Innovation Continue", description: "Intégrer les dernières technologies et méthodes pédagogiques pour une formation d'avant-garde." },
        { icon: ShieldCheck, title: "Qualité Garantie", description: "Maintenir les plus hauts standards de qualité dans tous nos processus académiques et administratifs." }
    ]

    return (
        <div className="min-h-screen bg-background font-sans text-foreground antialiased">
            <Helmet>
                <title>Vision & Mission - Université de Ngozi</title>
                <meta name="description" content="Découvrez la mission et la vision de l'Université de Ngozi : former, innover et servir pour transformer la société, avec une ambition d'excellence régionale à l'horizon 2030." />
            </Helmet>

            {/* Hero Section - Standard (ref: DroitEcoGestion) */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">L'Institution</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Notre Raison d'Être & Notre Ambition</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Vision & Mission</span>
                            <span className="block text-white mt-2">de l'Université de Ngozi</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Former, innover et servir pour transformer la société. Devenir un pôle d'excellence universitaire incontournable en Afrique de l'Est, reconnu pour son excellence académique et son impact positif sur la société.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Link to="/historique">
                                <Button className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                    Notre Histoire
                                    <ArrowRight className="w-5 h-5 ml-3" />
                                </Button>
                            </Link>
                            <Link to="/valeurs">
                                <Button variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm">
                                    Nos Valeurs
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Dateline Bar */}
            <div className="border-y border-border bg-secondary/5">
                <div className="container py-6 flex flex-wrap items-center gap-12 lg:gap-20">
                    {stats.map((stat, i) => (
                        <div key={i} className="flex items-center gap-3 group">
                            <span className="text-2xl font-black text-primary group-hover:text-accent transition-colors">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-[0.2em]">{stat.label}</span>
                            {i < stats.length - 1 && <span className="hidden lg:block h-4 w-px bg-border ml-4" />}
                        </div>
                    ))}
                </div>
            </div>

            <article className="container py-20 lg:py-32">
                <div className="grid lg:grid-cols-[300px_1fr] gap-20">
                    {/* Floating Navigation */}
                    <aside className="lg:sticky lg:top-32 lg:self-start hidden lg:block">
                        <nav className="space-y-2 mb-12">
                            <p className="text-[10px] uppercase tracking-[0.3em] font-black text-muted-foreground mb-6 pb-2 border-b-2 border-primary/10">
                                Sommaire
                            </p>
                            {["Vision", "Mission", "Organisation", "Aspirations"].map((item, i) => (
                                <a
                                    key={i}
                                    href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
                                    className="group flex items-center justify-between w-full py-3 text-xs font-black text-muted-foreground hover:text-primary transition-all border-r-2 border-transparent hover:border-primary pr-4"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-[10px] font-serif italic text-accent opacity-0 group-hover:opacity-100 transition-opacity">0{i + 1}</span>
                                        <span className="uppercase tracking-widest">{item}</span>
                                    </div>
                                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all" />
                                </a>
                            ))}
                        </nav>

                        <div className="bg-primary p-8 rounded-3xl text-primary-foreground relative overflow-hidden shadow-2xl">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12" />
                            <h4 className="text-[10px] uppercase tracking-[0.2em] font-black mb-6 text-accent">Nos Piliers</h4>
                            <ul className="space-y-4 text-[11px] font-medium border-t border-white/10 pt-4">
                                <li className="flex items-center gap-3">
                                    <div className="w-1.5 h-1.5 bg-accent rounded-full" />
                                    Excellence Académique
                                </li>
                                <li className="flex items-center gap-3">
                                    <div className="w-1.5 h-1.5 bg-accent rounded-full" />
                                    Recherche Appliquée
                                </li>
                                <li className="flex items-center gap-3">
                                    <div className="w-1.5 h-1.5 bg-accent rounded-full" />
                                    Service Communautaire
                                </li>
                            </ul>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="space-y-24">
                        {/* Vision Statement */}
                        <section id="vision" className="bg-primary/5 p-12 border border-primary/10 rounded-3xl relative overflow-hidden scroll-mt-32">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-full -mr-16 -mt-16" />
                            <div className="max-w-3xl relative">
                                <div className="flex items-center gap-2 mb-4">
                                    <Eye className="w-6 h-6 text-primary" />
                                    <h3 className="text-2xl font-serif font-black italic text-primary">Notre Vision</h3>
                                </div>
                                <p className="text-lg text-foreground/90 leading-relaxed font-bold italic mb-6">
                                   La vision  d’une institution fait référence à un idéal ou un « rêve » que cette institution poursuit et voudrait voir accompli. Ainsi, la vision de l’Université de Ngozi est celle de « former des cadres de moyen et de haut niveau, capables d’appréhender les problèmes des différents milieux où ils seront appelés à œuvrer et de trouver des solutions appropriées en contribuant à la promotion d’un développement durable et à l’éducation morale et humaine de la société ».
                                </p>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    Cette vision guide chaque décision académique et stratégique, plaçant l'humain et le développement durable au centre de notre mission d'enseignement et de recherche.
                                </p>
                            </div>
                        </section>

                        {/* Mission Section */}
                        <section id="mission">
                            <h2 className="text-3xl lg:text-4xl font-serif font-black italic text-foreground mb-8 flex items-center gap-3">
                                <div className="w-12 h-1 bg-accent rounded-full" />
                                Notre Mission
                            </h2>
                            <div className="border-l-4 border-accent pl-6 mb-12">
                                <p className="text-lg text-foreground/90 leading-relaxed italic border-b border-border/50 pb-6 mb-6">
                                    La mission explicite la raison d'être de l'institution en termes plus concrets. Ainsi, tout en restant synthétique, l’Université de Ngozi a pour missions de :
                                </p>
                                <ul className="grid sm:grid-cols-2 gap-x-12 gap-y-6">
                                    {[
                                        "Contribuer à l’éducation à la paix et à la réconciliation nationale",
                                        "Offrir un avenir plein d’espoir à la jeunesse de notre pays et des pays voisins",
                                        "Offrir une formation de niveau Baccalauréat, Master, voire Doctorat",
                                        "Promouvoir une recherche appliquée et fondamentale pour le développement socio-économique"
                                    ].map((m, i) => (
                                        <li key={i} className="flex items-start gap-4">
                                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                                                <Target className="w-3 h-3 text-primary" />
                                            </div>
                                            <span className="text-sm text-muted-foreground font-medium">{m}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </section>

                        {/* Organisation Section */}
                        <section id="organisation" className="scroll-mt-32">
                            <h2 className="text-3xl lg:text-4xl font-serif font-black italic text-foreground mb-10 flex items-center gap-3">
                                <div className="w-12 h-1 bg-accent rounded-full" />
                                Organisation Institutionnelle
                            </h2>
                            <div className="grid md:grid-cols-2 gap-12">
                                <div className="bg-secondary/5 p-8 rounded-3xl border border-border">
                                    <h3 className="text-lg font-black uppercase text-primary mb-6 flex items-center gap-2">
                                        <ShieldCheck className="w-5 h-5 text-accent" />
                                        Instances de Direction
                                    </h3>
                                    <ul className="space-y-3">
                                        {[
                                            "Assemblée générale",
                                            "Conseil d’Administration",
                                            "Conseil de Direction",
                                            "Conseil Rectoral",
                                            "Sénat Interne d’Assurance Qualité",
                                            "Conseil Académique",
                                            "Conseil pédagogique",
                                            "Conseil de la Recherche Scientifique",
                                            "Conseil de Faculté/d’Institut"
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-center gap-3 text-sm text-muted-foreground font-medium">
                                                <div className="w-1.5 h-1.5 bg-accent/40 rounded-full" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="bg-primary/5 p-8 rounded-3xl border border-primary/10 flex flex-col justify-center">
                                    <h3 className="text-lg font-black uppercase text-primary mb-4 flex items-center gap-2">
                                        <Rocket className="w-5 h-5 text-accent" />
                                        Offre Académique
                                    </h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                        L’organisation académique de l’Université de Ngozi repose sur une structure solide et diversifiée, couvrant l’enseignement universitaire classique et technique.
                                    </p>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-white/50 border border-border rounded-xl text-center">
                                            <div className="text-2xl font-black text-primary">5</div>
                                            <div className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Facultés</div>
                                        </div>
                                        <div className="p-4 bg-white/50 border border-border rounded-xl text-center">
                                            <div className="text-2xl font-black text-accent">5</div>
                                            <div className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">Instituts</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Aspirations (Vision 2030) */}
                        <section id="aspirations">
                            <h2 className="text-3xl lg:text-4xl font-serif font-black italic text-foreground mb-10 flex items-center gap-3">
                                <div className="w-12 h-1 bg-accent rounded-full" />
                                Nos Aspirations — Vision 2030
                            </h2>
                            <div className="space-y-8">
                                {aspirations.map((aspiration, idx) => (
                                    <div
                                        key={idx}
                                        className="group p-8 bg-secondary/5 rounded-3xl border border-border hover:border-accent/30 transition-all duration-300 hover:-translate-y-1"
                                    >
                                        <div className="flex items-start gap-6">
                                            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-all">
                                                <aspiration.icon className="w-7 h-7 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="text-xl font-black text-foreground mb-2">{aspiration.title}</h4>
                                                <p className="text-muted-foreground leading-relaxed">{aspiration.description}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Dimension Sociale & Engagement */}
                        <section id="engagements" className="grid md:grid-cols-2 gap-12 scroll-mt-32">
                            <div className="space-y-6">
                                <h4 className="text-sm font-black uppercase tracking-widest text-accent flex items-center gap-2">
                                    <HeartPulse className="w-5 h-5" /> Dimension Sociale
                                </h4>
                                <p className="text-base text-muted-foreground leading-relaxed">
                                    Plus qu'un centre d'enseignement, l'Université de Ngozi est un acteur du changement. Nous croyons que l'éducation est le levier le plus puissant pour briser les cycles de pauvreté et construire une paix durable entre les peuples.
                                </p>
                            </div>
                            <div className="bg-secondary/30 p-8 rounded-3xl flex flex-col justify-center border-l-4 border-accent">
                                <blockquote className="text-sm font-bold text-primary uppercase tracking-tight mb-2 italic">
                                    Engagement Solidaire
                                </blockquote>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    Nous soutenons les étudiants issus de milieux défavorisés par des bourses d'excellence et un accompagnement personnalisé.
                                </p>
                            </div>
                        </section>
                    </main>
                </div>
            </article>

            {/* Immersive CTA Footer */}
            <footer className="bg-primary text-white border-t-8 border-accent relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/5 -skew-x-12 origin-top-right pointer-events-none" />
                <div className="container py-24 text-center relative z-10">
                    <div className="max-w-2xl mx-auto space-y-10">
                        <div className="inline-flex items-center gap-6 mb-4">
                            <div className="h-px w-8 bg-accent" />
                            <span className="text-[11px] uppercase tracking-[0.4em] text-accent font-black">Rejoignez Notre Mission</span>
                            <div className="h-px w-8 bg-accent" />
                        </div>

                        <h2 className="text-4xl lg:text-6xl font-serif italic mb-8 leading-tight">
                            Ensemble, transformons <br /> <span className="text-accent underline decoration-accent/30 decoration-8 underline-offset-4">l'avenir du Burundi</span>.
                        </h2>

                        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                            <Link to="/faculte/fdseg">
                                <Button className="bg-accent text-accent-foreground px-12 py-8 text-sm font-black hover:bg-white hover:text-primary hover:-translate-y-2 transition-all duration-500 rounded-2xl transform shadow-2xl">
                                    Découvrir Nos Programmes
                                    <ArrowRight className="w-5 h-5 ml-3" />
                                </Button>
                            </Link>
                            <Link to="/admission">
                                <Button variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/5 px-12 py-8 text-sm font-black rounded-2xl transition-all">
                                    Nous Contacter
                                </Button>
                            </Link>
                        </div>

                        <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-medium mt-12">
                            © {new Date().getFullYear()} Université de Ngozi · Mission & Vision
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    )
}
